import { useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PALETTE, WEEKDAYS } from "@/lib/cal";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const defaultHours = () => WEEKDAYS.map((_, i) => ({ enabled: i < 5, start: "08:00", end: "17:00" }));
const empty = () => ({ name: "", role_title: "", phone: "", email: "", color: null, working_hours: defaultHours(), time_off: [], user_id: "" });

const AccountSelect = ({ f, setF, users }) => {
  const pick = (v) => {
    const u = users.find((x) => x.id === v);
    setF({ ...f, user_id: v === "none" ? "" : v, name: f.name || u?.name || "", email: f.email || u?.email || "" });
  };
  return (
    <div className="space-y-1.5 sm:col-span-2">
      <Label>Cont în aplicație (pentru „Calendarul meu”)</Label>
      <Select value={f.user_id || "none"} onValueChange={pick}>
        <SelectTrigger data-testid="staff-user-select"><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="none" data-testid="staff-user-select-none">Fără cont</SelectItem>
          {users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`staff-user-select-${u.id}`}>{u.name} · {u.email}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};

const HoursEditor = ({ hours, onChange }) => {
  const upd = (i, patch) => onChange(hours.map((h, j) => (j === i ? { ...h, ...patch } : h)));
  return (
    <div className="space-y-2" data-testid="working-hours-editor">
      {hours.map((h, i) => (
        <div key={i} className="grid grid-cols-[auto_90px_1fr] items-center gap-3">
          <Switch checked={h.enabled} onCheckedChange={(v) => upd(i, { enabled: v })} data-testid={`wh-enabled-${i}`} />
          <span className={cn("text-sm", !h.enabled && "text-muted-foreground")}>{WEEKDAYS[i]}</span>
          {h.enabled ? (
            <div className="flex items-center gap-2">
              <Input type="time" step="900" value={h.start} onChange={(e) => upd(i, { start: e.target.value })} className="h-9" data-testid={`wh-start-${i}`} />
              <span className="text-muted-foreground">–</span>
              <Input type="time" step="900" value={h.end} onChange={(e) => upd(i, { end: e.target.value })} className="h-9" data-testid={`wh-end-${i}`} />
            </div>
          ) : <span className="text-xs text-muted-foreground">Liber</span>}
        </div>
      ))}
    </div>
  );
};

const TimeOffEditor = ({ list, onChange }) => {
  const [n, setN] = useState({ start_date: "", end_date: "", reason: "" });
  const add = () => {
    if (!n.start_date) { toast.error("Alege data de început"); return; }
    const item = { ...n, end_date: n.end_date || n.start_date };
    if (item.end_date < item.start_date) { toast.error("Data de sfârșit e înaintea celei de început"); return; }
    onChange([...list, item].sort((a, b) => a.start_date.localeCompare(b.start_date)));
    setN({ start_date: "", end_date: "", reason: "" });
  };
  return (
    <div className="space-y-3" data-testid="time-off-editor">
      {list.map((t, i) => (
        <div key={t.id || i} className="flex items-center justify-between gap-2 rounded-lg border bg-background/50 px-3 py-2 text-sm" data-testid={`time-off-${i}`}>
          <span><b>{t.start_date}</b> → <b>{t.end_date}</b>{t.reason && <span className="text-muted-foreground"> · {t.reason}</span>}</span>
          <button onClick={() => onChange(list.filter((_, j) => j !== i))} className="rounded p-1 text-muted-foreground hover:text-destructive" data-testid={`time-off-remove-${i}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_1fr_1.3fr_auto]">
        <Input type="date" value={n.start_date} onChange={(e) => setN({ ...n, start_date: e.target.value })} data-testid="time-off-start-input" />
        <Input type="date" value={n.end_date} onChange={(e) => setN({ ...n, end_date: e.target.value })} data-testid="time-off-end-input" />
        <Input placeholder="Motiv (ex: Concediu)" value={n.reason} onChange={(e) => setN({ ...n, reason: e.target.value })} data-testid="time-off-reason-input" />
        <Button type="button" variant="outline" onClick={add} data-testid="time-off-add-button"><Plus className="h-4 w-4" /> Adaugă</Button>
      </div>
    </div>
  );
};

export const StaffDialog = ({ open, onOpenChange, member, onSaved, users = [] }) => {
  const [f, setF] = useState(empty());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(member ? { ...empty(), ...member } : empty()); }, [open, member]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.name.trim()) { toast.error("Numele este obligatoriu"); return; }
    setSaving(true);
    const { name, role_title, phone, email, color, working_hours, time_off, user_id } = f;
    const payload = { name, role_title, phone, email, color, working_hours, time_off, user_id: user_id || "" };
    try {
      if (member) await api.put(`/staff/${member.id}`, payload);
      else await api.post("/staff", payload);
      toast.success(member ? "Coleg actualizat" : "Coleg adăugat");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="staff-dialog">
        <DialogHeader><DialogTitle>{member ? "Editează coleg" : "Coleg nou"}</DialogTitle></DialogHeader>
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Nume *</Label><Input value={f.name} onChange={set("name")} data-testid="staff-name-input" /></div>
            <div className="space-y-1.5"><Label>Funcție</Label><Input value={f.role_title} onChange={set("role_title")} placeholder="ex: Tehnician instalații" data-testid="staff-role-input" /></div>
            <div className="space-y-1.5"><Label>Telefon</Label><Input value={f.phone} onChange={set("phone")} data-testid="staff-phone-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={f.email} onChange={set("email")} data-testid="staff-email-input" /></div>
            <AccountSelect f={f} setF={setF} users={users} />
          </div>
          <div className="space-y-2">
            <Label>Culoare în calendar</Label>
            <div className="flex flex-wrap gap-2">
              {PALETTE.map((c) => (
                <button key={c} type="button" onClick={() => setF({ ...f, color: c })} style={{ backgroundColor: c }} data-testid={`staff-color-${c.slice(1)}`}
                  className={cn("h-7 w-7 rounded-full transition-transform hover:scale-110", f.color === c && "ring-2 ring-foreground ring-offset-2 ring-offset-background")} />
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">Program de lucru</h3>
            <HoursEditor hours={f.working_hours} onChange={(wh) => setF({ ...f, working_hours: wh })} />
          </div>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">Concedii / zile libere</h3>
            <TimeOffEditor list={f.time_off} onChange={(t) => setF({ ...f, time_off: t })} />
          </div>
          <div className="flex justify-end border-t pt-4">
            <Button onClick={save} disabled={saving} className="font-semibold" data-testid="staff-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salvează
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
